import React, { useMemo, useState, Component } from 'react';
import { UserIcon, UsersIcon, BookIcon, BuildingIcon, DollarSignIcon, CalendarIcon, AlertTriangleIcon, PlusIcon, EditIcon, TrashIcon, CopyIcon, CheckIcon, XIcon, ChevronRightIcon, ArrowUpRightIcon, LayersIcon, UserCheckIcon, ClockIcon, CheckCircleIcon } from 'lucide-react';
import { StatusBadge } from '../StatusBadge';
import { formatCurrency } from '../utils/currencyFormatter';
import { getDefaultCurrency } from '../../utils/appSettings';
import { GLCodeSelector } from './GLCodeSelector';
import { OrgUnitSelector } from '../settings/orgunit/OrgUnitSelector';
import { flattenOrgUnits, sampleOrgUnits, type OrgUnit } from './sharedFinancialData';
type Delegation = {
  id: string;
  type: 'user' | 'role' | 'group';
  assignee: string;
  assigneeId: string;
  glCode: string;
  glName: string;
  scope: 'Global' | 'OrgUnit';
  orgUnitId?: string;
  orgUnitName?: string;
  orgUnitPath?: string;
  lowerLimit: number;
  upperLimit: number;
  canApproveAboveLimit: boolean;
  requiresFourEyes: boolean;
  secondApproverId?: string;
  secondApprover?: string;
  secondApproverType?: 'user' | 'role' | 'group';
  startDate: string;
  endDate: string | null;
  isActive: boolean;
  currency: string;
};
export function DelegationsManager({
  locale
}) {
  const [activeTab, setActiveTab] = useState('delegations');
  const [showForm, setShowForm] = useState(false);
  const [editingDelegation, setEditingDelegation] = useState<Delegation | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Get all OrgUnits from shared data
  const allOrgUnits = flattenOrgUnits(sampleOrgUnits);
  // Sample data for delegations
  const [delegations, setDelegations] = useState<Delegation[]>([{
    id: 'del-001',
    type: 'user',
    assignee: 'John Smith',
    assigneeId: 'user-001',
    glCode: '1000.1100.1110',
    glName: 'Cash and Cash Equivalents',
    scope: 'Global',
    lowerLimit: 0,
    upperLimit: 5000,
    canApproveAboveLimit: false,
    requiresFourEyes: false,
    startDate: '2023-01-01',
    endDate: '2023-12-31',
    isActive: true,
    currency: 'AUD'
  }, {
    id: 'del-002',
    type: 'role',
    assignee: 'Finance Manager',
    assigneeId: 'role-002',
    glCode: '5000',
    glName: 'Expenses',
    scope: 'Global',
    lowerLimit: 5000,
    upperLimit: 25000,
    canApproveAboveLimit: false,
    requiresFourEyes: true,
    secondApproverId: 'user-002',
    secondApprover: 'Jane Doe',
    secondApproverType: 'user',
    startDate: '2023-01-01',
    endDate: null,
    isActive: true,
    currency: 'AUD'
  }, {
    id: 'del-003',
    type: 'user',
    assignee: 'Jane Doe',
    assigneeId: 'user-002',
    glCode: '5000.5100',
    glName: 'Operating Expenses',
    scope: 'OrgUnit',
    orgUnitId: 'ou-apac-sg',
    orgUnitName: 'Singapore Office',
    orgUnitPath: 'Global Technologies Inc. > APAC Region > Singapore Office',
    lowerLimit: 0,
    upperLimit: 10000,
    canApproveAboveLimit: true,
    requiresFourEyes: false,
    startDate: '2023-01-01',
    endDate: '2023-12-31',
    isActive: true,
    currency: 'SGD'
  }, {
    id: 'del-004',
    type: 'group',
    assignee: 'Finance Approvers',
    assigneeId: 'group-001',
    glCode: '5000.5200',
    glName: 'Travel Expenses',
    scope: 'OrgUnit',
    orgUnitId: 'ou-latam-mx',
    orgUnitName: 'Mexico Office',
    orgUnitPath: 'Global Technologies Inc. > LATAM Region > Mexico Office',
    lowerLimit: 10000,
    upperLimit: 50000,
    canApproveAboveLimit: false,
    requiresFourEyes: true,
    secondApproverId: 'group-002',
    secondApprover: 'Executive Approvers',
    secondApproverType: 'group',
    startDate: '2023-01-01',
    endDate: null,
    isActive: true,
    currency: 'MXN'
  }]);
  // Sample data for GL restrictions
  const [glRestrictions, setGlRestrictions] = useState([{
    id: 'res-001',
    type: 'user',
    assignee: 'John Smith',
    assigneeId: 'user-001',
    glCode: '5000.5200',
    glName: 'Travel Expenses',
    canApprove: false
  }, {
    id: 'res-002',
    type: 'role',
    assignee: 'Junior Accountant',
    assigneeId: 'role-003',
    glCode: '1000.1200',
    glName: 'Non-Current Assets',
    canApprove: false
  }]);
  // Sample data for user leave status
  const [userLeaveStatus, setUserLeaveStatus] = useState([{
    id: 'leave-001',
    userId: 'user-001',
    userName: 'John Smith',
    isOnLeave: true,
    leaveStartDate: '2023-10-15',
    leaveEndDate: '2023-10-30',
    shouldSkip: true
  }, {
    id: 'leave-002',
    userId: 'user-002',
    userName: 'Jane Doe',
    isOnLeave: false,
    leaveStartDate: null,
    leaveEndDate: null,
    shouldSkip: false
  }]);
  // Sample data for approval rules
  const [approvalRules, setApprovalRules] = useState([{
    id: 'rule-001',
    delegationId: 'del-001',
    delegationName: 'John Smith - Cash and Cash Equivalents',
    approvalLevel: 1,
    requiresMultipleApprovers: false,
    escalationUserId: 'user-002',
    escalationUserName: 'Jane Doe',
    autoApproveAfter: 3
  }, {
    id: 'rule-002',
    delegationId: 'del-002',
    delegationName: 'Finance Manager - Expenses',
    approvalLevel: 2,
    requiresMultipleApprovers: true,
    escalationUserId: null,
    escalationUserName: null,
    autoApproveAfter: 5
  }]);
  // Sample data for approval groups
  const [approvalGroups, setApprovalGroups] = useState([{
    id: 'group-001',
    name: 'Finance Approvers',
    members: [{
      id: 'user-001',
      name: 'John Smith'
    }, {
      id: 'user-002',
      name: 'Jane Doe'
    }]
  }, {
    id: 'group-002',
    name: 'Executive Approvers',
    members: [{
      id: 'user-003',
      name: 'Michael Johnson'
    }, {
      id: 'user-004',
      name: 'Sarah Williams'
    }]
  }]);
  // Sample data for users and roles
  const users = [{
    id: 'user-001',
    name: 'John Smith'
  }, {
    id: 'user-002',
    name: 'Jane Doe'
  }, {
    id: 'user-003',
    name: 'Michael Johnson'
  }, {
    id: 'user-004',
    name: 'Sarah Williams'
  }];
  const roles = [{
    id: 'role-001',
    name: 'Administrator'
  }, {
    id: 'role-002',
    name: 'Finance Manager'
  }, {
    id: 'role-003',
    name: 'Junior Accountant'
  }, {
    id: 'role-004',
    name: 'Department Head'
  }];
  // Sample data for GL codes
  const glCodes = [{
    id: 'gl-001',
    code: '1000',
    name: 'Assets',
    children: [{
      id: 'gl-002',
      code: '1000.1100',
      name: 'Current Assets',
      children: [{
        id: 'gl-003',
        code: '1000.1100.1110',
        name: 'Cash and Cash Equivalents'
      }, {
        id: 'gl-004',
        code: '1000.1100.1120',
        name: 'Accounts Receivable'
      }]
    }, {
      id: 'gl-005',
      code: '1000.1200',
      name: 'Non-Current Assets'
    }]
  }, {
    id: 'gl-006',
    code: '2000',
    name: 'Liabilities',
    children: [{
      id: 'gl-007',
      code: '2000.2100',
      name: 'Current Liabilities'
    }]
  }, {
    id: 'gl-008',
    code: '3000',
    name: 'Equity'
  }, {
    id: 'gl-009',
    code: '4000',
    name: 'Revenue'
  }, {
    id: 'gl-010',
    code: '5000',
    name: 'Expenses',
    children: [{
      id: 'gl-011',
      code: '5000.5100',
      name: 'Operating Expenses'
    }, {
      id: 'gl-012',
      code: '5000.5200',
      name: 'Travel Expenses'
    }]
  }];
  // Available currencies
  const currencies = [{
    code: 'AUD',
    name: 'Australian Dollar'
  }, {
    code: 'USD',
    name: 'US Dollar'
  }, {
    code: 'EUR',
    name: 'Euro'
  }, {
    code: 'GBP',
    name: 'British Pound'
  }, {
    code: 'SGD',
    name: 'Singapore Dollar'
  }, {
    code: 'MXN',
    name: 'Mexican Peso'
  }];
  // Handler for creating a new delegation
  const handleCreateDelegation = () => {
    setEditingDelegation(null);
    setShowForm(true);
  };
  // Handler for editing a delegation
  const handleEditDelegation = (delegation: Delegation) => {
    setEditingDelegation(delegation);
    setShowForm(true);
  };
  // Handler for duplicating a delegation
  const handleDuplicateDelegation = (delegation: Delegation) => {
    const newDelegation: Delegation = {
      ...delegation,
      id: `del-${Date.now()}`,
      assignee: `Copy of ${delegation.assignee}`
    };
    setDelegations([...delegations, newDelegation]);
  };
  // Handler for deactivating a delegation
  const handleDeactivateDelegation = (delegationId: string) => {
    setDelegations(delegations.map(d => d.id === delegationId ? {
      ...d,
      isActive: false
    } : d));
  };
  // Handler for deleting a delegation
  const handleDeleteDelegation = (delegationId: string) => {
    setDeletingId(delegationId);
    setShowDeleteConfirm(true);
  };
  const confirmDelete = () => {
    if (deletingId) {
      setDelegations(delegations.filter(d => d.id !== deletingId));
      setShowDeleteConfirm(false);
      setDeletingId(null);
    }
  };
  // Handler for form submission
  const handleSubmitForm = (formData: Partial<Delegation>) => {
    if (editingDelegation) {
      // Update existing delegation
      setDelegations(delegations.map(d => d.id === editingDelegation.id ? {
        ...(formData as Delegation),
        id: editingDelegation.id
      } : d));
    } else {
      // Create new delegation
      const newDelegation: Delegation = {
        ...(formData as Delegation),
        id: `del-${Date.now()}`
      };
      setDelegations([...delegations, newDelegation]);
    }
    setShowForm(false);
    setEditingDelegation(null);
  };
  const globalDelegations = delegations.filter(d => d.scope === 'Global');
  const orgUnitDelegations = delegations.filter(d => d.scope === 'OrgUnit');
  // Render the Delegations tab content
  const renderDelegationsTab = () => {
    if (showForm) {
      return <DelegationForm delegation={editingDelegation} users={users} roles={roles} groups={approvalGroups} glCodes={glCodes} currencies={currencies} onSubmit={handleSubmitForm} onCancel={() => {
        setShowForm(false);
        setEditingDelegation(null);
      }} locale={locale} data-id="element-8190" />;
    }
    return <div className="space-y-6" data-id="element-8191">
        {/* Global Delegations */}
        <div className="bg-white shadow rounded-lg" data-id="element-8192">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-8193">
            <div className="flex justify-between items-center" data-id="element-8194">
              <div data-id="element-8195">
                <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-8196">
                  Global Delegations
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-8197">
                  Global approval authorities available to all organisation
                  units
                </p>
              </div>
              <button onClick={handleCreateDelegation} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-8198">
                <PlusIcon className="h-4 w-4 mr-2" data-id="element-8199" />
                Add Delegation
              </button>
            </div>
          </div>

          {globalDelegations.length === 0 ? <div className="px-4 py-12 text-center" data-id="element-8200">
              <p className="text-sm text-gray-500" data-id="element-8201">
                No global delegations configured.
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-8202">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-8203">
                <thead className="bg-gray-50" data-id="element-8204">
                  <tr data-id="element-8205">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8206">
                      User/Role/Group
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8207">
                      GL Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8208">
                      Limits
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8209">
                      Approval
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8210">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8211">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-8212">
                  {globalDelegations.map(delegation => <tr key={delegation.id} data-id="element-8213">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-8214">
                        <div className="flex items-center" data-id="element-8215">
                          {delegation.type === 'user' ? <UserIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-8216" /> : delegation.type === 'role' ? <UsersIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-8217" /> : <LayersIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-8218" />}
                          <div className="text-sm font-medium text-gray-900" data-id="element-8219">
                            {delegation.assignee}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-8220">
                        <div className="text-sm text-gray-900" data-id="element-8221">
                          {delegation.glCode}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-8222">
                          {delegation.glName}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-8223">
                        <div className="text-sm text-gray-900" data-id="element-8224">
                          {formatCurrency(delegation.lowerLimit, delegation.currency, locale)}{' '}
                          -{' '}
                          {formatCurrency(delegation.upperLimit, delegation.currency, locale)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-8225">
                        {delegation.requiresFourEyes ? <div className="flex items-center text-xs text-indigo-600" data-id="element-8226">
                            <UsersIcon className="h-3 w-3 mr-1" data-id="element-8227" />
                            Four Eyes
                          </div> : <div className="flex items-center text-xs text-gray-500" data-id="element-8228">
                            <UserCheckIcon className="h-3 w-3 mr-1" data-id="element-8229" />
                            Single
                          </div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-8230">
                        {delegation.isActive ? <StatusBadge status="success" text="Active" data-id="element-8231" /> : <StatusBadge status="error" text="Inactive" data-id="element-8232" />}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-8233">
                        <div className="flex justify-end space-x-2" data-id="element-8234">
                          <button onClick={() => handleEditDelegation(delegation)} className="text-indigo-600 hover:text-indigo-900" data-id="element-8235">
                            <EditIcon className="h-4 w-4" data-id="element-8236" />
                          </button>
                          <button onClick={() => handleDuplicateDelegation(delegation)} className="text-blue-600 hover:text-blue-900" data-id="element-8237">
                            <CopyIcon className="h-4 w-4" data-id="element-8238" />
                          </button>
                          <button onClick={() => handleDeleteDelegation(delegation.id)} className="text-red-600 hover:text-red-900" data-id="element-8239">
                            <TrashIcon className="h-4 w-4" data-id="element-8240" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>

        {/* OrgUnit Delegations */}
        <div className="bg-white shadow rounded-lg" data-id="element-8241">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-8242">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-8243">
              Organisation Unit Delegations
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-8244">
              Approval authorities specific to individual organisation units
            </p>
          </div>

          {orgUnitDelegations.length === 0 ? <div className="px-4 py-12 text-center" data-id="element-8245">
              <p className="text-sm text-gray-500" data-id="element-8246">
                No organisation unit delegations configured.
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-8247">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-8248">
                <thead className="bg-gray-50" data-id="element-8249">
                  <tr data-id="element-8250">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8251">
                      Organisation Unit
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8252">
                      User/Role/Group
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8253">
                      GL Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8254">
                      Limits
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8255">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8256">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-8257">
                  {orgUnitDelegations.map(delegation => <tr key={delegation.id} data-id="element-8258">
                      <td className="px-6 py-4" data-id="element-8259">
                        <div className="text-sm font-medium text-gray-900" data-id="element-8260">
                          {delegation.orgUnitName}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-8261">
                          {delegation.orgUnitPath}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-8262">
                        <div className="flex items-center" data-id="element-8263">
                          {delegation.type === 'user' ? <UserIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-8264" /> : delegation.type === 'role' ? <UsersIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-8265" /> : <LayersIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-8266" />}
                          <div className="text-sm font-medium text-gray-900" data-id="element-8267">
                            {delegation.assignee}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-8268">
                        <div className="text-sm text-gray-900" data-id="element-8269">
                          {delegation.glCode}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-8270">
                          {delegation.glName}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-8271">
                        <div className="text-sm text-gray-900" data-id="element-8272">
                          {formatCurrency(delegation.lowerLimit, delegation.currency, locale)}{' '}
                          -{' '}
                          {formatCurrency(delegation.upperLimit, delegation.currency, locale)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-8273">
                        {delegation.isActive ? <StatusBadge status="success" text="Active" data-id="element-8274" /> : <StatusBadge status="error" text="Inactive" data-id="element-8275" />}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-8276">
                        <div className="flex justify-end space-x-2" data-id="element-8277">
                          <button onClick={() => handleEditDelegation(delegation)} className="text-indigo-600 hover:text-indigo-900" data-id="element-8278">
                            <EditIcon className="h-4 w-4" data-id="element-8279" />
                          </button>
                          <button onClick={() => handleDuplicateDelegation(delegation)} className="text-blue-600 hover:text-blue-900" data-id="element-8280">
                            <CopyIcon className="h-4 w-4" data-id="element-8281" />
                          </button>
                          <button onClick={() => handleDeleteDelegation(delegation.id)} className="text-red-600 hover:text-red-900" data-id="element-8282">
                            <TrashIcon className="h-4 w-4" data-id="element-8283" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>
      </div>;
  };
  // Render the GL Restrictions tab content (keeping existing implementation)
  const renderGLRestrictionsTab = () => {
    return <div className="space-y-4" data-id="element-8284">
        <div className="flex justify-between items-center mb-4" data-id="element-8285">
          <div data-id="element-8286">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-8287">
              GL Restrictions
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-8288">
              Define where users or roles are restricted from approving
            </p>
          </div>
          <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700" data-id="element-8289">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-8290" />
            Add Restriction
          </button>
        </div>
        {/* Existing GL Restrictions table */}
        <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg" data-id="element-8291">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-8292">
            <thead className="bg-gray-50" data-id="element-8293">
              <tr data-id="element-8294">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8295">
                  User/Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8296">
                  GL Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8297">
                  Restriction
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8298">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-8299">
              {glRestrictions.map(restriction => <tr key={restriction.id} data-id="element-8300">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-8301">
                    <div className="flex items-center" data-id="element-8302">
                      {restriction.type === 'user' ? <UserIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-8303" /> : <UsersIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-8304" />}
                      <div className="text-sm font-medium text-gray-900" data-id="element-8305">
                        {restriction.assignee}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-8306">
                    <div className="text-sm text-gray-900" data-id="element-8307">
                      {restriction.glCode}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-8308">
                      {restriction.glName}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-8309">
                    {restriction.canApprove ? <StatusBadge status="success" text="Can Approve" data-id="element-8310" /> : <StatusBadge status="error" text="Cannot Approve" data-id="element-8311" />}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-8312">
                    <div className="flex justify-end space-x-2" data-id="element-8313">
                      <button className="text-indigo-600 hover:text-indigo-900" data-id="element-8314">
                        <EditIcon className="h-4 w-4" data-id="element-8315" />
                      </button>
                      <button className="text-red-600 hover:text-red-900" data-id="element-8316">
                        <TrashIcon className="h-4 w-4" data-id="element-8317" />
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>;
  };
  // Render other tabs (keeping existing implementations)
  const renderLeaveStatusTab = () => {
    return <div className="space-y-4" data-id="element-8318">
        <div className="flex justify-between items-center mb-4" data-id="element-8319">
          <div data-id="element-8320">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-8321">
              User Delegation Status
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-8322">
              Manage user leave and delegation status
            </p>
          </div>
        </div>
        {/* Existing leave status table */}
      </div>;
  };
  const renderApprovalRulesTab = () => {
    return <div className="space-y-4" data-id="element-8323">
        <div className="flex justify-between items-center mb-4" data-id="element-8324">
          <div data-id="element-8325">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-8326">
              Approval Rules
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-8327">
              Configure approval workflows and escalation rules
            </p>
          </div>
        </div>
        {/* Existing approval rules table */}
      </div>;
  };
  const renderApprovalGroupsTab = () => {
    return <div className="space-y-4" data-id="element-8328">
        <div className="flex justify-between items-center mb-4" data-id="element-8329">
          <div data-id="element-8330">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-8331">
              Approval Groups
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-8332">
              Manage reusable groups of approvers
            </p>
          </div>
        </div>
        {/* Existing approval groups grid */}
      </div>;
  };
  return <div className="space-y-6" data-id="element-8333">
      <div className="mb-8" data-id="element-8334">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-8335">Delegations</h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-8336">
          Configure and manage financial approval authority at global and
          organisation unit levels
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-8337">
        <nav className="-mb-px flex space-x-8" data-id="element-8338">
          <button onClick={() => setActiveTab('delegations')} className={`${activeTab === 'delegations' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-8339">
            Delegations
          </button>
          <button onClick={() => setActiveTab('gl-restrictions')} className={`${activeTab === 'gl-restrictions' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-8340">
            GL Restrictions
          </button>
          <button onClick={() => setActiveTab('leave-status')} className={`${activeTab === 'leave-status' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-8341">
            Leave Status
          </button>
          <button onClick={() => setActiveTab('approval-rules')} className={`${activeTab === 'approval-rules' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-8342">
            Approval Rules
          </button>
          <button onClick={() => setActiveTab('approval-groups')} className={`${activeTab === 'approval-groups' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-8343">
            Groups
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6" data-id="element-8344">
        {activeTab === 'delegations' && renderDelegationsTab()}
        {activeTab === 'gl-restrictions' && renderGLRestrictionsTab()}
        {activeTab === 'leave-status' && renderLeaveStatusTab()}
        {activeTab === 'approval-rules' && renderApprovalRulesTab()}
        {activeTab === 'approval-groups' && renderApprovalGroupsTab()}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && <div className="fixed z-10 inset-0 overflow-y-auto" data-id="element-8345">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-8346">
            <div className="fixed inset-0 transition-opacity" data-id="element-8347">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-8348"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" data-id="element-8349">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-8350">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-8351">
                <div className="sm:flex sm:items-start" data-id="element-8352">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10" data-id="element-8353">
                    <AlertTriangleIcon className="h-6 w-6 text-red-600" data-id="element-8354" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left" data-id="element-8355">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-8356">
                      Delete Delegation
                    </h3>
                    <div className="mt-2" data-id="element-8357">
                      <p className="text-sm text-gray-500" data-id="element-8358">
                        Are you sure you want to delete this delegation? This
                        action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-8359">
                <button type="button" onClick={confirmDelete} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-8360">
                  Delete
                </button>
                <button type="button" onClick={() => setShowDeleteConfirm(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-8361">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}
// Delegation Form Component (simplified - keeping core structure)
function DelegationForm({
  delegation,
  users,
  roles,
  groups,
  glCodes,
  currencies,
  onSubmit,
  onCancel,
  locale
}) {
  const [formData, setFormData] = useState({
    type: delegation?.type || 'user',
    assigneeId: delegation?.assigneeId || '',
    assignee: delegation?.assignee || '',
    glCode: delegation?.glCode || '',
    glName: delegation?.glName || '',
    scope: delegation?.scope || 'Global',
    orgUnitId: delegation?.orgUnitId || '',
    orgUnitName: delegation?.orgUnitName || '',
    orgUnitPath: delegation?.orgUnitPath || '',
    lowerLimit: delegation?.lowerLimit || 0,
    upperLimit: delegation?.upperLimit || 0,
    canApproveAboveLimit: delegation?.canApproveAboveLimit || false,
    requiresFourEyes: delegation?.requiresFourEyes || false,
    secondApproverId: delegation?.secondApproverId || '',
    secondApprover: delegation?.secondApprover || '',
    secondApproverType: delegation?.secondApproverType || 'user',
    startDate: delegation?.startDate || new Date().toISOString().split('T')[0],
    endDate: delegation?.endDate || '',
    isActive: delegation?.isActive !== undefined ? delegation.isActive : true,
    currency: delegation?.currency || getDefaultCurrency().code
  });
  const [errors, setErrors] = useState({});
  // Create flattened segments for GL code lookup
  const flattenedSegments = useMemo(() => {
    const result = [];
    const flatten = (codes, parentPath = '') => {
      codes.forEach(code => {
        const fullPath = parentPath ? `${parentPath}.${code.code}` : code.code;
        result.push({
          ...code,
          fullGLPath: fullPath,
          segmentName: code.name,
          segmentCode: code.code
        });
        if (code.children && code.children.length > 0) {
          flatten(code.children, fullPath);
        }
      });
    };
    flatten(glCodes);
    return result;
  }, [glCodes]);
  const handleChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    if (name === 'scope' && value === 'Global') {
      setFormData({
        ...formData,
        scope: value,
        orgUnitId: '',
        orgUnitName: '',
        orgUnitPath: ''
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  const handleOrgUnitChange = (orgUnitId, orgUnit) => {
    setFormData({
      ...formData,
      orgUnitId,
      orgUnitName: orgUnit?.name || '',
      orgUnitPath: orgUnit?.path || ''
    });
  };
  const validateForm = () => {
    const newErrors = {};
    if (!formData.assigneeId) newErrors.assigneeId = `Please select a ${formData.type}`;
    if (!formData.glCode) newErrors.glCode = 'Please select a GL code';
    if (formData.scope === 'OrgUnit' && !formData.orgUnitId) {
      newErrors.orgUnitId = 'Please select an organisation unit';
    }
    if (formData.upperLimit <= formData.lowerLimit) {
      newErrors.upperLimit = 'Upper limit must be greater than lower limit';
    }
    if (!formData.startDate) newErrors.startDate = 'Start date is required';
    if (!formData.currency) newErrors.currency = 'Currency is required';
    if (formData.requiresFourEyes && !formData.secondApproverId) {
      newErrors.secondApproverId = 'Please select a second approver';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = e => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-8362">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-8363">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-8364">
          {delegation ? 'Edit Delegation' : 'Create Delegation'}
        </h3>
      </div>
      <form onSubmit={handleSubmit} data-id="element-8365">
        <div className="px-4 py-5 sm:p-6" data-id="element-8366">
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-8367">
            {/* Scope */}
            <div className="sm:col-span-3" data-id="element-8368">
              <label className="block text-sm font-medium text-gray-700" data-id="element-8369">
                Scope <span className="text-red-500" data-id="element-8370">*</span>
              </label>
              <div className="mt-1" data-id="element-8371">
                <select name="scope" value={formData.scope} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-8372">
                  <option value="Global" data-id="element-8373">Global</option>
                  <option value="OrgUnit" data-id="element-8374">Organisation Unit</option>
                </select>
              </div>
              <p className="mt-1 text-xs text-gray-500" data-id="element-8375">
                {formData.scope === 'Global' ? 'Available to all organisation units' : 'Specific to selected organisation unit'}
              </p>
            </div>

            {/* OrgUnit Selector */}
            {formData.scope === 'OrgUnit' && <div className="sm:col-span-3" data-id="element-8376">
                <OrgUnitSelector value={formData.orgUnitId} onChange={handleOrgUnitChange} label="Organisation Unit" placeholder="Select organisation unit" required={true} showPath={true} error={errors.orgUnitId} data-id="element-8377" />
              </div>}

            {/* Rest of form fields - keeping existing structure */}
            {/* Assignment type, assignee, GL code, limits, etc. */}
            <div className="sm:col-span-6" data-id="element-8378">
              <p className="text-sm text-gray-500" data-id="element-8379">
                Form fields for assignment, GL code, limits, and approval
                requirements would continue here...
              </p>
            </div>
          </div>
        </div>
        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 flex justify-end space-x-3" data-id="element-8380">
          <button type="button" onClick={onCancel} className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-8381">
            Cancel
          </button>
          <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700" data-id="element-8382">
            {delegation ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </div>;
}